import type React from 'react';
import i18n from '@shared/i18n';
import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { ApiError } from '@/services/requests/core/ApiError';
import { request as __request } from '@/services/requests/core/request';
import type { PropertyDocument } from './property-document.types';

export type { PropertyDocument } from './property-document.types';

interface UploadLinkResponse {
  upload_url: { url: string; fields: Record<string, string> };
  document_id: string;
}

export interface UsePropertyDocumentsState {
  documents: PropertyDocument[];
  loading: boolean;
  error: string | null;
}

type SetState = React.Dispatch<React.SetStateAction<UsePropertyDocumentsState>>;

/**
 * Normalizes a documents payload into an array.
 *
 * @param data - Raw payload from the documents endpoint
 * @returns Documents array (empty when payload is not a list)
 */
function toDocumentList(data: unknown): PropertyDocument[] {
  return Array.isArray(data) ? (data as PropertyDocument[]) : [];
}

/**
 * Whether a documents load failure means the property has no documents yet.
 *
 * @param err - Error thrown while loading documents
 * @returns True when the API returned 404
 */
function isNoDocumentsError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * Maps a load error to the next hook state, treating 404 as empty.
 *
 * @param err - Error thrown while loading documents
 * @returns State to apply after the failed load
 */
function documentsErrorState(err: unknown): UsePropertyDocumentsState {
  if (isNoDocumentsError(err)) {
    return { documents: [], loading: false, error: null };
  }
  const fallback = i18n.t('hooks.failedToLoadDocuments', { ns: 'features/properties' });
  const message = err instanceof Error ? err.message : fallback;
  return { documents: [], loading: false, error: message };
}

/**
 * Loads the property's documents into hook state.
 *
 * @param agencyId - Owning agency identifier
 * @param propertyId - Target property identifier
 * @param setState - State setter for the documents hook
 */
export async function loadDocuments(
  agencyId: string,
  propertyId: string,
  setState: SetState,
): Promise<void> {
  if (!agencyId || !propertyId) return;
  setState((prev) => ({ ...prev, loading: true, error: null }));
  try {
    const data = await __request<PropertyDocument[]>(OpenAPIHousien, {
      method: 'GET',
      url: '/agencies/{agencyId}/properties/{propertyId}/documents',
      path: { agencyId, propertyId },
    });
    setState({ documents: toDocumentList(data), loading: false, error: null });
  } catch (err) {
    setState(documentsErrorState(err));
  }
}

/**
 * Requests a presigned upload link for a new document.
 *
 * @param agencyId - Owning agency identifier
 * @param propertyId - Target property identifier
 * @param filename - Name of the file to upload
 * @param type - Document MIME or type label
 * @returns Upload link response, or null on failure
 */
export async function requestUploadLink(
  agencyId: string,
  propertyId: string,
  filename: string,
  type: string,
): Promise<UploadLinkResponse | null> {
  try {
    return await __request<UploadLinkResponse>(OpenAPIHousien, {
      method: 'POST',
      url: '/agencies/{agencyId}/properties/{propertyId}/documents/upload-link',
      path: { agencyId, propertyId },
      body: { filename, type },
      mediaType: 'application/json',
    });
  } catch {
    return null;
  }
}

/**
 * Deletes a document and removes it from hook state on success.
 *
 * @param agencyId - Owning agency identifier
 * @param propertyId - Target property identifier
 * @param documentId - Identifier of the document to delete
 * @param setState - State setter for the documents hook
 * @returns True when the deletion succeeded
 */
export async function removeDocument(
  agencyId: string,
  propertyId: string,
  documentId: string,
  setState: SetState,
): Promise<boolean> {
  try {
    await __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agencies/{agencyId}/properties/{propertyId}/documents/{documentId}',
      path: { agencyId, propertyId, documentId },
    });
    setState((prev) => ({
      ...prev,
      documents: prev.documents.filter((d) => d.document_id !== documentId),
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Renames a document's display filename and updates local state on success.
 *
 * @param agencyId - Owning agency identifier
 * @param propertyId - Target property identifier
 * @param documentId - Identifier of the document to rename
 * @param filename - New display filename
 * @param setState - State setter for the documents hook
 * @returns True when the rename succeeded
 */
export async function renameDocumentRequest(
  agencyId: string,
  propertyId: string,
  documentId: string,
  filename: string,
  setState: SetState,
): Promise<boolean> {
  try {
    const updated = await __request<PropertyDocument>(OpenAPIHousien, {
      method: 'PATCH',
      url: '/agencies/{agencyId}/properties/{propertyId}/documents/{documentId}',
      path: { agencyId, propertyId, documentId },
      body: { filename },
      mediaType: 'application/json',
    });
    setState((prev) => ({
      ...prev,
      documents: prev.documents.map((d) =>
        d.document_id === documentId ? { ...d, filename: updated.filename || filename } : d,
      ),
    }));
    return true;
  } catch {
    return false;
  }
}
